<template>
  <div
    v-if="column.rows?.length > 0"
    class="mm-column"
    :class="{
      'mm-column--divider': showDivider,
      'mm-column--filter-gap': reserveFilterSpace
    }"
    :style="columnStyle"
  >
    <!--
      The heading element is rendered even when the label is blank: the client
      is explicit that the heading space stays reserved so columns line up
      (walkthrough 04:37). It is also kept to a single line for the same
      reason — a heading too long for the column width truncates with a
      tooltip rather than wrapping, which would push this column's rows below
      its neighbours' (walkthrough 07:55).
    -->
    <div class="mm-column__heading">
      <span v-if="column.heading" class="mm-column__heading-text" :title="column.heading">
        <span class="mm-column__heading-label">{{ column.heading }}</span>
        <span class="mm-column__rule" />
      </span>
    </div>

    <ul v-if="column.rows?.length > 0" class="mm-column__rows">
      <li
        v-for="(row, index) in column.rows"
        :key="index"
        class="mm-row"
        :class="{ 'mm-row--spacer': !row.label }"
        :style="{ paddingLeft: indentPx(row) + 'px' }"
      >
        <!-- A blank label is a deliberate vertical spacer (walkthrough 04:01). -->
        <template v-if="row.label">
          <a
            v-if="row.url"
            :href="row.url"
            class="mm-row__link"
            @click="onNavigate($event, row)"
          >{{ row.label }}</a>
          <span v-else class="mm-row__label">{{ row.label }}</span>
        </template>
      </li>
    </ul>
  </div>
</template>

<script>
/**
 * One mega menu column. Shared by the public header and the admin preview, so
 * the two can never drift.
 *
 * Text is bound with {{ }} throughout — never v-html. Menu copy is
 * admin-authored and rendered on every page of the site.
 */
import { followInternalLink } from '~/helpers/internal-link'

export default {
  name: 'MegaMenuColumn',

  props: {
    column: {
      type: Object,
      required: true
    },
    fixedWidth: {
      type: Boolean,
      default: false
    },

    width: {
      type: Number,
      default: null
    },
    /** px per indent level; mirrors config/mega-menu.php indent_step_px */
    indentStep: {
      type: Number,
      default: 10
    },
    /**
     * A divider separates this column from something to its right. The panel
     * decides; the stored `divider_right` only says the admin asked for one.
     */
    dividerAllowed: {
      type: Boolean,
      default: true
    },
    /**
     * Keep the bottom of this column clear of the FILTER SEARCH button, which
     * is centred across the column block and therefore only overlaps the
     * middle column(s) (walkthrough 10:45-11:22).
     */
    reserveFilterSpace: {
      type: Boolean,
      default: false
    },
    /** Preview swallows navigation so a click cannot leave the editor. */
    inert: {
      type: Boolean,
      default: false
    }
  },

  computed: {
    showDivider () {
      return this.dividerAllowed && !!this.column.divider_right
    },

    /**
     * flex-grow proportional to the matrix width (basis 0) so every column in
     * the block divides the block's width equally, which lets the whole block
     * scale fluidly with the viewport. Tablet range (768-1279) overrides via
     * !important CSS below; desktop uses the matrix width as the grow ratio
     * (same value for every column of a given menu, so shares are equal).
     */
    columnStyle () {
      if (this.width && this.fixedWidth) {
        return { flex: `0 0 ${this.width}px`, width: `${this.width}px`, minWidth: 0 }
      }

      return this.width ? { flex: `${this.width} 1 0`, minWidth: 0 } : null
    }
  },

  methods: {
    indentPx (row) {
      const level = Number(row.indent) || 0

      return Math.max(0, level) * this.indentStep
    },

    onNavigate (event, row) {
      if (this.inert) {
        event.preventDefault()
        return
      }

      followInternalLink(event, this.$router)
      this.$emit('navigate', row)
    }
  }
}
</script>

<style scoped lang="scss">
/* Type tokens transcribed from XD "Design Instructions" (Artboard - 4). */
/* Tablet range (768-1279). A column that shares its row with the promo pane
   (.mm-column--tablet-fit) flexes like desktop, taking its share from the inline
   matrix width. Otherwise the column is locked at 190px, or grows to share the
   full row when the panel wraps (see MegaMenuPanel). The divider on the right
   edge is left to the admin's divider_right flag (via the .mm-column--divider
   class). It is trimmed to stop at the top of the Filter Search button so it
   never extends below the button. */
@media (min-width: 768px) and (max-width: 1279px) {
  .mm-column:not(.mm-column--tablet-fit) {
    flex: 0 0 auto !important;
    width: 190px !important;
    max-width: 190px !important;
    min-width: 190px !important;
    height: auto !important;
  }

  .mm-column--divider::after {
    height: calc(100% - 90px) !important;
  }

  .mm-column--tablet-fit.mm-column--divider::after {
    height: calc(100% - 58px) !important;
  }
}

.mm-column {
  /* A column is a full-height flex stack so the row list can take the leftover
     space and scroll inside it. Without that, a column with more rows than the
     fixed 580px panel holds simply ran off the bottom edge and under the
     FILTER SEARCH button (walkthrough 09:43-11:22). */
  position: relative;
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 25px 20px 26px;
  box-sizing: border-box;

  &--divider::after {
    /* 1px x 522px on the right edge (walkthrough 08:08), vertically CENTRED in
       the 580px panel: 580 - 522 = 58 = 2 x 29. The client describes "a little
       gap from the top and bottom" (08:42), and a 5x measurement of the 128%
       preview gives ~35 top / ~31 bottom against a 528-unit line — equal within
       error, and nothing like the 25/33 that anchoring to the column's top
       padding produces. Height is fixed: it does not follow content. */
    content: '';
    position: absolute;
    top: 29px;
    right: 0;
    width: 1px;
    height: 522px;
    background-color: #383838;
  }

  &--filter-gap {
    /* FILTER SEARCH sits 24px off the bottom and is ~37px tall; 76px clears it
       with a little breathing room. */
    padding-bottom: 76px;
  }
}

.mm-column__heading {
  /* Reserved even when empty, so columns stay vertically aligned: 11px text +
     4px gap + 1px rule. A blank heading reserves the space but draws no rule,
     which is how the XD renders its heading-less columns.
     flex, not block: the child has to shrink-wrap so the rule below can be a
     percentage of the TEXT, and an inline-block child would sit on a text
     baseline and add ~9px of descender leading that a blank heading does not
     have — which knocked headed and heading-less columns out of alignment.
     justify-content: center matches the promo pane header treatment so the
     heading sits above the rule inside a wide column instead of left-hugging
     a sparsely-filled 1-col + promo layout (client QA). */
  display: flex;
  justify-content: center;
  align-items: flex-start;
  min-height: 16px;
  margin-bottom: 20.5px;
  flex: 0 0 auto;
}

.mm-column__heading-text {
  /* Shrink-wraps the text so the rule below can be a percentage OF THE TEXT
     rather than of the column. Measured off the XD "Design Instructions" at 9x:
     column header 895 units wide / rule 568 = 63%, inset 167 left and 160 right;
     promo header 980 / 587 = 59.9%, inset 198 and 195. So: 60%, centred.
     min-width: 0 lets it shrink past its content so an over-long heading
     truncates instead of widening the column. */
  display: block;
  max-width: 100%;
  min-width: 0;
  font-family: Arial, Helvetica, sans-serif;
  font-size: 11px;
  line-height: 1;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #535353;
}

.mm-column__heading-label {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.mm-column__rule {
  display: block;
  width: 60%;
  height: 1px;
  margin: 4px auto 0;
  background-color: #535353;
}

.mm-column__rows {
  flex: 1 1 auto;
  /* Required for a flex child to shrink below its content and actually scroll. */
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  scrollbar-width: thin;
  list-style: none;
  margin: 0;
  padding: 0;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-thumb {
    background-color: #cfcfcf;
    border-radius: 3px;
  }
}

@media (min-width: 1280px) {
  /* Panel height follows content on desktop, so the divider tracks the column
     instead of the old fixed 522px. */
  .mm-column--divider::after {
    height: calc(100% - 58px);
  }
}

/* Tablet and desktop: once the rows hit the top or bottom of their scroll, the
   wheel or swipe carries on instead of being swallowed by the column. In a
   wrapped tablet panel that is what brings the promo pane below into view.
   Must stay after the base .mm-column__rows rule, which it overrides. */
@media (min-width: 768px) {
  .mm-column__rows {
    overscroll-behavior: auto;
  }
}

.mm-row {
  font-family: Arial, Helvetica, sans-serif;
  font-size: 14px;
  line-height: 1;
  color: #383838;
  margin-bottom: 15px;

  &--spacer {
    /* Occupies a row's worth of height and nothing else. */
    height: 14px;
    margin-bottom: 15px;
  }
}

.mm-row__link {
  color: #383838;
  text-decoration: none;

  &:hover,
  &:focus-visible {
    text-decoration: underline;
  }
}
</style>
